import React, { useCallback, useMemo, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import { makeStyles } from '@mui/styles'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { usePermissions, useIsAcAdmin } from '@hooks/useHasPermission'
import { useEnabledFeatures } from '@hooks/useFeature'
import { BUILDING_INFO_GROUPS, isGroupVisible, isSectionVisible } from '@/account/buildingInfoGroups'
import { ANALYTICS_CATEGORIES } from '@/analytics/analyticsCategories'
import { getBuildingReports, getPortfolioReports, ReportOption } from '@/analytics/reportRegistry'
import { buildMenuTabList, MenuTabSection } from '@/pages/menuTabList'
import { matchesTokens, searchTokens } from '@utils/tokenSearch'

interface MenuSearchEntry {
  key: string
  label: string
  /** Where the entry lives in the nav — matched alongside the label, so a
   * category name query ("analytics", "contacts") surfaces its items/tabs. */
  breadcrumb: string
  pathname: string
}

interface MenuSearchProps {
  /** The permission-filtered nav tree Menu already computed. */
  menu: any[]
}

const MAX_RESULTS = 15

const SEARCH_ICON = (
  <InputAdornment position="start">
    <SearchIcon fontSize="small" style={{ color: '#707070' }} />
  </InputAdornment>
)

/** Every whitespace-separated token must appear in the entry's label or breadcrumb. */
const matchesQuery = (entry: MenuSearchEntry, query: string) =>
  matchesTokens(`${entry.label} ${entry.breadcrumb}`, searchTokens(query))

const MenuSearch: React.FC<MenuSearchProps> = ({ menu }) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const permissions = usePermissions()
  const isAcAdmin = useIsAcAdmin()
  const features = useEnabledFeatures()
  const { selectedGroupId, selectedPropertyId } = useSelector((state: any) => state.profile)
  const [inputValue, setInputValue] = useState('')

  const entries = useMemo(() => {
    const {
      ViewAnalytics = false,
      ActivateCustomerFeedback = false,
      PortfolioTeam = false,
      ManageGroups = false,
      ManageContacts = false,
      ManagePropertyInfo = false,
      Analytics = false,
    } = permissions || {}
    const hasBuilding = !!selectedPropertyId
    const scope = hasBuilding ? `/b/${selectedPropertyId}` : ''
    // Keyed by label+path: the same page reachable as a nav leaf and as a tab
    // (COI → Insurance Dashboard) stays listed once per distinct label.
    const byKey = new Map<string, MenuSearchEntry>()
    const add = (label: string, breadcrumb: string, pathname?: string) => {
      if (!label || !pathname) return
      const key = `${label}|${pathname}`
      if (!byKey.has(key)) byKey.set(key, { key, label, breadcrumb, pathname })
    }

    // 1. Nav leaves from the tree Menu computed — permissions, scoping, and
    // suppressed items are already resolved there.
    const walk = (items: any[], trail: string[]) => {
      items.forEach((item) => {
        if (item.show === false || item.disabled) return
        if (item.child) {
          walk(item.child, [...trail, item.primaryText])
          return
        }
        add(item.primaryText, trail.join(' › '), item.pathname)
      })
    }
    walk(menu, [])

    // 2. The tab strips behind those leaves. Each section's parent gate
    // mirrors the visibility of the nav item the tab strip hangs off; the
    // per-tab `show` flags carry the tab-level permissions.
    const tabList = buildMenuTabList({
      scope,
      permissions,
      isAcAdmin,
      features,
      // Building-specific flags are unknown outside the tab shell; the safe
      // defaults hide the Industrial-only tab and target the Activate-managed
      // PM Library.
      BuildingType: '',
      PM: false,
      accountGroupId: selectedGroupId,
    })
    const sectionGates: Record<string, boolean> = {
      accountInfo: !!selectedGroupId && (ManageGroups || PortfolioTeam),
      contacts: hasBuilding && ManageContacts,
      library: hasBuilding && isAcAdmin,
      masterLibrary: ActivateCustomerFeedback,
      scans: ActivateCustomerFeedback,
      surveyLibrary: ActivateCustomerFeedback,
      reportBuilder: ActivateCustomerFeedback,
      asset: hasBuilding,
      coi: hasBuilding,
      notifications: hasBuilding,
      dataArchive: hasBuilding,
    }
    Object.entries(tabList).forEach(([type, section]: [string, MenuTabSection]) => {
      if (!sectionGates[type]) return
      section.list.forEach((tab) => {
        if (tab.show === false) return
        add(tab.label, section.label, tab.path)
      })
    })

    // 3. Building Info in-page tabs (the buildingInfo tab list above is empty
    // outside its shell — it needs an active group).
    if (hasBuilding && ManagePropertyInfo) {
      BUILDING_INFO_GROUPS.filter((group) => !group.hiddenFromNav && isGroupVisible(group, permissions, isAcAdmin)).forEach(
        (group) => {
          group.sections
            .filter((section) => isSectionVisible(section, permissions, false, isAcAdmin))
            .forEach((section) => {
              add(section.label, `Building Info › ${group.label}`, `${scope}/buildingInfo/${group.slug}/${section.slug}`)
            })
        }
      )
    }

    // 4. Analytics reports, grouped the way the sidebar categories group them.
    if (hasBuilding && Analytics) {
      const reports = getBuildingReports(scope, permissions, features) as ReportOption[]
      ANALYTICS_CATEGORIES.forEach((category) => {
        category.values.forEach((value) => {
          const option = reports.find((report) => report.value === value)
          if (option) add(option.label, `Analytics › ${category.label}`, option.path)
        })
      })
    }

    // 5. Portfolio Analytics reports, grouped the way its sidebar categories are.
    if (selectedGroupId && (ViewAnalytics || PortfolioTeam)) {
      const reports = getPortfolioReports(`/p/${selectedGroupId}`, permissions, features)
      ANALYTICS_CATEGORIES.forEach((category) => {
        category.portfolioValues?.forEach((value) => {
          const option = reports.find((report) => report.value === value)
          if (option) add(option.label, `Portfolio Analytics › ${category.label}`, option.path)
        })
      })
    }

    return Array.from(byKey.values())
  }, [menu, permissions, isAcAdmin, features, selectedGroupId, selectedPropertyId])

  const filterOptions = useCallback(
    (options: MenuSearchEntry[], { inputValue: query }: { inputValue: string }) =>
      query.trim() ? options.filter((option) => matchesQuery(option, query)).slice(0, MAX_RESULTS) : [],
    []
  )

  const handleChange = useCallback(
    (_: unknown, option: MenuSearchEntry | null) => {
      if (!option) return
      setInputValue('')
      navigate(option.pathname, { state: { _scrollToTop: true } })
    },
    [navigate]
  )

  const handleInputChange = useCallback((_: unknown, value: string, reason: string) => {
    // 'reset' fires on selection — handled in handleChange, which clears.
    if (reason !== 'reset') setInputValue(value)
  }, [])

  const getOptionLabel = useCallback((option: MenuSearchEntry) => option.label, [])

  // The 'reset' guard above keeps a selection from writing its label back into
  // the field, but it also swallows clearOnBlur's reset — clear explicitly.
  const handleBlur = useCallback(() => setInputValue(''), [])

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: MenuSearchEntry) => (
      <li {...props} key={option.key}>
        <Box>
          <Typography className={classes.optionLabel}>{option.label}</Typography>
          {!!option.breadcrumb && <Typography className={classes.optionBreadcrumb}>{option.breadcrumb}</Typography>}
        </Box>
      </li>
    ),
    [classes]
  )

  const renderInput = useCallback(
    (params: any) => (
      <TextField
        {...params}
        placeholder="Search navigation"
        variant="outlined"
        size="small"
        InputProps={{
          ...params.InputProps,
          startAdornment: SEARCH_ICON,
        }}
      />
    ),
    []
  )

  return (
    <Autocomplete
      className={classes.root}
      options={entries}
      value={null}
      inputValue={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onInputChange={handleInputChange}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderInput={renderInput}
      noOptionsText={inputValue.trim() ? 'No matching pages' : 'Type to search pages and tabs'}
      blurOnSelect
      clearOnBlur
      forcePopupIcon={false}
    />
  )
}

const useStyles = makeStyles(() => ({
  root: {
    width: 200,
    margin: '8px 0 2px',
    '& .MuiOutlinedInput-root': {
      borderRadius: 6,
      fontSize: 13,
      backgroundColor: '#F5F5F5',
      padding: '2px 8px',
    },
  },
  optionLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#211F1F',
    lineHeight: 1.25,
  },
  optionBreadcrumb: {
    fontSize: 12,
    color: '#707070',
    lineHeight: 1.25,
  },
}))

export default React.memo(MenuSearch)
